import axios from "axios";
import { notifyDvaPending, requestDvaNote } from "src/utils/dvaInterceptor";

const axiosInstance = axios.create();

const MAX_REQUEST_PERF_EVENTS = 400;

const getNow = () => {
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    return performance.now();
  }
  return Date.now();
};

const resolveRequestUrl = (config) => {
  const raw = config?.url || "unknown";
  if (typeof window === "undefined") {
    return raw;
  }

  try {
    const parsed = new URL(raw, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (_error) {
    return raw;
  }
};

const resolvePerfStore = () => {
  if (typeof window === "undefined") {
    return null;
  }

  if (!window.__arkaduRequestPerf) {
    window.__arkaduRequestPerf = {
      events: [],
      byId: {},
      lastUpdatedAt: null,
    };
  }

  return window.__arkaduRequestPerf;
};

const resolvePersistedMode = () => {
  if (typeof window === "undefined") {
    return "user";
  }

  try {
    const storedSettings = localStorage.getItem("settings");
    if (!storedSettings) {
      return "user";
    }

    const parsed = JSON.parse(storedSettings);
    return parsed?.adminMode ? "admin" : "user";
  } catch (_error) {
    return "user";
  }
};

const attachDefaultMode = (data) => {
  const resolvedMode = resolvePersistedMode();

  if (data instanceof FormData) {
    const hasCustomer = data.has("customer_id");
    const hasMode = data.has("mode");
    if (hasCustomer && !hasMode) {
      data.append("mode", resolvedMode);
    }
    return data;
  }

  if (data && typeof data === "object" && !Array.isArray(data)) {
    const hasCustomer = data.customer_id != null;
    if (hasCustomer && data.mode == null) {
      return {
        ...data,
        mode: resolvedMode,
      };
    }
  }

  return data;
};

const recordRequestPerf = ({
  id,
  method,
  url,
  durationMs,
  status,
  ok,
  startedAt,
  endedAt,
}) => {
  const store = resolvePerfStore();
  if (!store) {
    return;
  }

  const event = {
    id,
    method: (method || "GET").toUpperCase(),
    url,
    durationMs: Math.max(0, Math.round(Number(durationMs || 0))),
    status: status || "ERR",
    ok: Boolean(ok),
    startedAt: startedAt || Date.now(),
    endedAt: endedAt || Date.now(),
  };

  store.events.push(event);
  if (store.events.length > MAX_REQUEST_PERF_EVENTS) {
    store.events = store.events.slice(-MAX_REQUEST_PERF_EVENTS);
  }
  store.byId[event.id] = event;
  store.lastUpdatedAt = Date.now();

  try {
    window.dispatchEvent(
      new CustomEvent("arkadu:request-perf", {
        detail: event,
      }),
    );
  } catch (_error) {
    // ignore event dispatch errors
  }
};

axiosInstance.defaults.withCredentials = true;
axiosInstance.defaults.xsrfHeaderName = "X-CSRFToken";
axiosInstance.defaults.xsrfCookieName = "csrftoken";

// if (accessToken) {
//   axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
//   axios.defaults.withCredentials = true;
// }
axiosInstance.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }
  config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : "";

  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch"].includes(method)) {
    config.data = attachDefaultMode(config.data);
  }

  const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  config.__arkaduPerf = {
    requestId,
    startedPerfAt: getNow(),
    startedAt: Date.now(),
    method: config.method,
    url: resolveRequestUrl(config),
  };

  return config;
});
const buildDvaRetryConfig = (config, note) => {
  const retryConfig = { ...config, __dvaNoteRetried: true };
  let data = retryConfig.data;

  // axios ya serializó config.data a JSON string antes de fallar; hay que
  // parsearlo de vuelta para no perder los campos originales (customer_id, etc).
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (_error) {
      data = {};
    }
  }

  if (data instanceof FormData) {
    data.append("request_note", note);
  } else if (data && typeof data === "object" && !Array.isArray(data)) {
    data = { ...data, request_note: note };
  } else {
    data = { request_note: note };
  }

  retryConfig.data = data;
  return retryConfig;
};

axiosInstance.interceptors.response.use(
  (response) => {
    const perf = response?.config?.__arkaduPerf;
    if (perf) {
      recordRequestPerf({
        id: perf.requestId,
        method: perf.method,
        url: perf.url,
        durationMs: getNow() - perf.startedPerfAt,
        status: response?.status,
        ok: true,
        startedAt: perf.startedAt,
        endedAt: Date.now(),
      });
    }

    if (response.status === 302) {
      return Promise.reject({ status: 302 });
    }

    if (response.data?.code === "approval_pending") {
      notifyDvaPending(response.data.data || {});
    }

    return response;
  },
  (error) => {
    const perf = error?.config?.__arkaduPerf;
    if (perf) {
      recordRequestPerf({
        id: perf.requestId,
        method: perf.method,
        url: perf.url,
        durationMs: getNow() - perf.startedPerfAt,
        status: error?.response?.status || "ERR",
        ok: false,
        startedAt: perf.startedAt,
        endedAt: Date.now(),
      });
    }

    const dvaCode = error?.response?.data?.code;
    if (
      dvaCode === "approval_request_note_required" &&
      error.config &&
      !error.config.__dvaNoteRetried
    ) {
      return requestDvaNote({
        url: error.config.url,
        message: error.response.data?.message,
      }).then((note) => {
        if (!note) {
          return Promise.reject(error.response);
        }
        return axiosInstance(buildDvaRetryConfig(error.config, note));
      });
    }

    if (error.response) {
      if (error.response) {
        return Promise.reject(error.response);
      } else if (error.response.status) {
        return Promise.reject(error.response.statusText);
      } else {
        Promise.reject("Something went wrong");
      }
    }
    // Promise.reject(
    //   (error.response && error.response.data) || (error.response && error.response.status) 'Something went wrong',
    //   error.response.status,
    // );
  },
);

const handleRes = async (res) => {
  try {
    if (res.data.res === 1) {
      return res.data;
    } else {
      // console.log(res);
      const error = new Error(
        (res.data && res.data.message) || "Api fetch Error",
      );
      error.info = await res.json();
      error.state = res.status;
      throw error;
    }
  } catch (e) {
    // console.log(e);
    const error = new Error(`Error api handleRes ${e}`);
    throw error;
    // No res data. key error
  }
};

const apiPost = async (url, data, config) => {
  try {
    const res = await axiosInstance.post(url, data, config);
    return handleRes(res);
  } catch (e) {
    console.log(e);
    throw e;
  }
};

const apiGet = async (url, config) => {
  try {
    const res = await axiosInstance.get(url, config);
    return handleRes(res);
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export default axiosInstance;
export { apiGet, apiPost, axiosInstance as axios };
